import React, { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { Link, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { Formik } from "formik";
import * as Yup from "yup";
import {
  getForIndividualsStep,
  updateForIndividualsStep,
} from "../../../api/axios";
import CmsCircularProgress from "../../Common/CircularProgress";
import { showToast } from "../../../Utills/toast";
import FormGroup from "../../Common/FormGroup";

const id = 1;

// Validation Schema
const validationSchema = Yup.object({
  step1: Yup.string()
    .required("Step 1 content is required"),
  step2: Yup.string()
    .required("Step 2 content is required"),
  step3: Yup.string()
    .required("Step 3 content is required"),
  step4: Yup.string()
    .required("Step 4 content is required"),
});

export default function EditSteps() {
  const { partnerId } = useParams();
  const [data, setStepcard] = useState({
    step1: "",
    step2: "",
    step3: "",
    step4: "",
    index: null,
  });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  const getInitialValues = () => ({
    step1: data.step1 || "",
    step2: data.step2 || "",
    step3: data.step3 || "",
    step4: data.step4 || "",
  });

  const fetchStepsData = async () => {
    setIsLoading(true);
    try {
      const res = await getForIndividualsStep();
      console.log(res);
      setStepcard(res.data.data[0]);
    } catch (error) {
      console.log(error);
      showToast({
        type: 'error',
        message: error?.response?.data?.message || "Failed to fetch steps data. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStepsData();
  }, []);

  const handleSubmit = async (values) => {
    setSaving(true);

    const formData = {
      step1: values.step1,
      step2: values.step2,
      step3: values.step3,
      step4: values.step4,
    };

    try {
      const res = await updateForIndividualsStep(formData);
      console.log(res);
      if (res.status === 200) {
        showToast({
          type: 'success',
          message: res?.data?.message || 'Steps section has been updated successfully!',
        });
        console.log(res.data);
      } else {
        showToast({
          type: 'error',
          message: res?.data?.message || 'Failed to update steps section. Please try again.',
        });
      }
    } catch (err) {
      console.log(err);
      showToast({
        type: 'error',
        message: err?.response?.data?.message || 'Failed to update steps section. Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {isLoading ? <CmsCircularProgress /> : 
        <FormWrapper>
          <Title>{Number(id) ? 'Edit' : 'Add'} Steps Section</Title>
          <SliderDiv>
            <Formik
              initialValues={getInitialValues()}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize={true}
            >
              {({
                values,
                errors,
                touched,
                handleSubmit,
                getFieldProps,
                isSubmitting,
              }) => (
                <Form noValidate onSubmit={handleSubmit}>
                  {/* Step 1 */}
                  <SubTitle>Step 1</SubTitle>
                  <FormGroup 
                    label='Enter a new content' 
                    requiredField
                    error={touched.step1 && errors.step1 ? errors.step1 : ''}
                  >
                    <Form.Control
                      as="textarea"
                      rows={3}
                      {...getFieldProps('step1')}
                      placeholder="Enter step 1 content here..."
                      isInvalid={touched.step1 && !!errors.step1}
                    />
                  </FormGroup>

                  {/* Step 2 */}
                  <SubTitle className="mt-3">Step 2</SubTitle>
                  <FormGroup 
                    label='Enter a new content' 
                    requiredField
                    error={touched.step2 && errors.step2 ? errors.step2 : ''}
                  >
                    <Form.Control
                      as="textarea"
                      rows={3}
                      {...getFieldProps('step2')}
                      placeholder="Enter step 2 content here..."
                      isInvalid={touched.step2 && !!errors.step2}
                    />
                  </FormGroup>

                  {/* Step 3 */}
                  <SubTitle className="mt-3">Step 3</SubTitle>
                  <FormGroup 
                    label='Enter a new content' 
                    requiredField
                    error={touched.step3 && errors.step3 ? errors.step3 : ''}
                  >
                    <Form.Control
                      as="textarea"
                      rows={3}
                      {...getFieldProps('step3')}
                      placeholder="Enter step 3 content here..."
                      isInvalid={touched.step3 && !!errors.step3}
                    />
                  </FormGroup>

                  {/* Step 4 */}
                  <SubTitle className="mt-3">Step 4</SubTitle>
                  <FormGroup 
                    label='Enter a new content' 
                    requiredField
                    error={touched.step4 && errors.step4 ? errors.step4 : ''}
                  >
                    <Form.Control
                      as="textarea"
                      rows={3}
                      {...getFieldProps('step4')}
                      placeholder="Enter step 4 content here..."
                      isInvalid={touched.step4 && !!errors.step4}
                    />
                  </FormGroup>

                  <ButtonWrapper className="mt-3">
                    <Link to={`/${partnerId}/forindividuals/step/view/${id}`}>
                      <Button variant="secondary" className="w-100">Go Back</Button>
                    </Link>
                    <Button 
                      variant="primary"
                      type="submit"
                      disabled={saving || isSubmitting}
                      className="w-100"
                    >
                      {saving ? "Saving..." : "Save Changes"}
                    </Button>
                  </ButtonWrapper>
                </Form>
              )}
            </Formik>
          </SliderDiv>
        </FormWrapper>
      }
    </>
  );
}

const SliderDiv = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
`;

const FormWrapper = styled.div`
  @media (min-width:576px) {
    padding: 1rem;
  }
  @media (min-width:768px) {
    padding: 1.5rem;
  }
  padding: .5rem;
`;

const Title = styled.h3`
  text-align: center;
  padding: 10px 0;
`;

const SubTitle = styled.h5``;

const ButtonWrapper = styled.div`
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  @media (max-width: 890px) {
    gap: 2rem;
  }
  @media (max-width: 425px) {
    gap: 1rem;
  }
`;
